import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useMerchantAuth } from '../../../contexts/MerchantAuthContext';
import FlutterServicesAPI from '../../../services/flutterServices';
import { merchantAuthAPI } from '../../../services/merchantAPI';
import './Header.css';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { merchant, isAuthenticated, loading } = useMerchantAuth();
  const [showWhatsAppSmsAuth, setShowWhatsAppSmsAuth] = React.useState(true);

  React.useEffect(() => {
    const checkAuthVisibility = async () => {
      try {
        const response = await merchantAuthAPI.checkAuthVisibility();
        if (response.data && typeof response.data.show_whatsapp_sms_auth === 'boolean') {
          setShowWhatsAppSmsAuth(response.data.show_whatsapp_sms_auth);
        }
      } catch (error) {
        console.error('❌ Header: Failed to check auth visibility:', error);
      }
    };
    checkAuthVisibility();
  }, []);



  // Function to check if a link is active
  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  // Function to get active class
  const getActiveClass = (path) => {
    return isActive(path) ? 'nav-link active' : 'nav-link';
  };

  // Function to scroll to top of the page
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Handle login button click in Flutter app
  const handleFlutterLoginClick = () => {
    if (isAuthenticated()) {
      navigate('/dashboard');
    } else {
      navigate('/login');
    }
  };

  // Function to handle profile icon click
  const handleProfileClick = () => {
    navigate('/dashboard');
  };

  // Function to scroll to features section
  const scrollToFeatures = () => {
    const element = document.getElementById('features-section');
    if (element) {
      const headerOffset = 80; // Adjust based on header height
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // Handle Features menu click
  const handleFeaturesClick = (e) => {
    if (location.pathname === '/') {
      e.preventDefault();
      scrollToFeatures();
    }
  };

  // Function to scroll to case studies section
  const scrollToCaseStudies = () => {
    const element = document.getElementById('case-studies-section');
    if (element) {
      const headerOffset = 80; // Adjust based on header height
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // Handle Case Studies menu click
  const handleCaseStudiesClick = (e) => {
    if (location.pathname === '/') {
      e.preventDefault();
      scrollToCaseStudies();
    }
  };

  return (
    <nav className="navbar navbar-expand-sm bg-dark navbar-dark">
      <div className={`container-fluid ${FlutterServicesAPI.isFlutterApp() ? 'ps-0' : ''}`}>
        <Link
          className="navbar-brand"
          to={location.pathname === '/guest-gallery' || !showWhatsAppSmsAuth ? '#' : '/'}
          onClick={(e) => {
            if (location.pathname === '/guest-gallery' || !showWhatsAppSmsAuth) {
              e.preventDefault(); // Prevent navigation on guest page or during app review
            } else {
              scrollToTop();
            }
          }}
        >
          <img
            src="/assets/img/glammify-logo.png"
            alt="Glammify Logo"
            className="img-fluid"
          />
        </Link>
        {/* Mobile Download App Button */}
        {!FlutterServicesAPI.isFlutterApp() && (
          <Link
            sx={{
              fontSize: { xs: '10px !important', sm: 'var(--font-size-small)' }, // mobile only
            }}
            className="btn-download-mobile-app btn btn-primary-app d-sm-none"
            to="/appdownload"
            onClick={scrollToTop}
          >
            Download App
          </Link>
        )}
        {/* In Flutter Contact Us page, show Login / Sign up button instead of hamburger */}
        {FlutterServicesAPI.isFlutterApp() && location.pathname === '/contact-us' ? (
          <button
            className="btn btn-sm-ft"
            type="button"
            onClick={handleFlutterLoginClick}
          >
            Login / Sign up
          </button>
        ) : (
          !FlutterServicesAPI.isFlutterApp() && (
            <button
              className="navbar-toggler"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#collapsibleNavbar"
            >
              <span className="navbar-toggler-icon"></span>
            </button>
          )
        )}
        <div className="collapse navbar-collapse" id="collapsibleNavbar">
          <ul className="navbar-nav main-menu ms-auto">
            <li className="nav-item">
              <Link className={getActiveClass('/')} to="/" onClick={scrollToTop}>Home</Link>
            </li>
            {/* Hide pricing link in Flutter app */}
            {!FlutterServicesAPI.isFlutterApp() && (
              <>
                <li className="nav-item">
                  <Link className={getActiveClass('/pricing')} to="/pricing" onClick={scrollToTop}>Pricing</Link>
                </li>
                <li className="nav-item">
                  <Link className="nav-link" to="/#features-section" onClick={handleFeaturesClick}>Features</Link>
                </li>
              </>
            )}
            <li className="nav-item">
              <Link className="nav-link" to="/#case-studies-section" onClick={handleCaseStudiesClick}>Case Study</Link>
            </li>
            <li className="nav-item">
              <Link className={getActiveClass('/blogs')} to="/blogs" onClick={scrollToTop}>Blog</Link>
            </li>

            <li className="nav-item">
              <Link className={getActiveClass('/contact-us')} to="/contact-us" onClick={scrollToTop}>Contact us</Link>
            </li>
            {isAuthenticated() && (
              <li className="nav-item">
                <Link className={getActiveClass('/dashboard')} to="/dashboard" onClick={scrollToTop}>Dashboard</Link>
              </li>
            )}
          </ul>
          <ul className="navbar-nav ms-auto align-items-center">
            <li className="nav-item d-none d-sm-flex align-items-center me-2">
              <Link className="btn btn-primary-web" to="/appdownload" onClick={scrollToTop}>
                Download App
              </Link>
            </li>
            {loading ? (
              // Show loader while authentication state is being determined
              <li className="nav-item">
                <div className="auth-loader">
                  <div className="spinner-border spinner-border-sm text-light" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              </li>
            ) : isAuthenticated() ? (
              // Show greeting and profile icon when merchant is logged in
              <li className="nav-item d-flex align-items-center">
                <span className="user-greeting">
                  Hi {merchant?.name ? merchant.name.split(' ')[0] : (merchant?.email ? merchant.email.split('@')[0] : 'User')}
                </span>
                <button
                  className="profile-icon"
                  onClick={handleProfileClick}
                >
                  {merchant?.name ? merchant.name[0].toUpperCase() : (merchant?.email ? merchant.email[0].toUpperCase() : 'M')}
                </button>
              </li>
            ) : (
              // Show login and try demo when merchant is not logged in
              <>
                {/* <li className="nav-item me-2 mn-auto login-lnk">
                  <Link className={getActiveClass('/login')} to="/login" onClick={scrollToTop}>
                    Login
                  </Link>
                </li> */}
                <li className="nav-item">
                  <Link className="btn btn-primary-web" to="/login" onClick={scrollToTop}>
                    Login / Sign up
                  </Link>
                </li>
              </>
            )}
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Header;
